import { Component, Inject, OnInit } from '@angular/core';
import { FormGroup, FormBuilder, Validators } from '@angular/forms';
import { AddressService, Country } from '../../service/address.service';
import { Router, ActivatedRoute } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { BioVerifyComponent } from '../../shared/bio-verify.component';
import { takeUntil, debounceTime } from 'rxjs/operators';
import { Address, AddressSuggestion } from '../../shared/model/address';
import { BrandingService } from '../../service/branding.service';
import { VerificationClientService } from 'src/app/service/verification-client.service';
import { DOCUMENT } from '@angular/common';
import { VerificationType } from '../../shared/model/invitation';
import { InvitationService } from 'src/app/service/invitation.service';

@Component({
  selector: 'app-address-capture',
  templateUrl: './address-capture.component.html',
  styleUrls: ['./address-capture.component.css']
})
export class AddressCaptureComponent extends BioVerifyComponent implements OnInit {

  addressForm: FormGroup;
  addressSuggestions: AddressSuggestion[] = [];
  selectedCountryCode: string;
  autoCompletionCountries: Country[];

  get logoUrl(): string {
    return this.brandingService.logoUrl;
  }

  get allCountries(): Country[] {
    return this.addressService.allCountries;
  }

  get postcodeRequired(): boolean {
    return this.addressForm && this.addressForm.value && ['NZ', 'AU', 'GB'].includes(this.addressForm.value.otherCountry);
  }

  get disabled(): boolean {
    if (this.selectedCountryCode === 'OTHER') {
      const address =  this.getEnteredAddress();
      const postcodeRequired = address && ['NZ', 'AU', 'GB'].includes(address.country);
      if (address && address.streetNumber && address.streetName && (address.suburb || address.city) && address.country && (!postcodeRequired || address.postcode)) {
        return false;
      }
      return true;
    } else {
      const addressText = this.addressForm.value.fullAddress;
      const matchingSuggestion = this.addressSuggestions.find(suggestion => suggestion.fullAddress === addressText);
      if (matchingSuggestion) {
        return false;
      }
      return true;
    }
  }

  private invitationId: string;
  private isSaving = false;
  private captureUrl = '';
  private auPayrollSuperConsent = 'false';
  private verificationType: VerificationType;

  constructor(
    private fb: FormBuilder,
    private addressService: AddressService,
    private invitationService: InvitationService,
    private route: ActivatedRoute,
    private router: Router,
    public dialog: MatDialog,
    private brandingService: BrandingService,
    private verificationClientService: VerificationClientService,
    @Inject(DOCUMENT) private document: Document
  ) {
    super();
  }

  ngOnInit() {
    this.invitationId = this.route.snapshot.paramMap.get('invitationId');
    this.captureUrl = this.route.snapshot.queryParamMap.get('captureUrl');
    this.auPayrollSuperConsent = this.route.snapshot.queryParamMap.get('auPayrollSuperConsent');
    this.verificationType = this.route.snapshot.queryParamMap.get('verificationType') as VerificationType;
    const autoCompletionCountries =  this.addressService.autoCompletionCountries;
    const isUK = this.verificationType && (this.verificationType.startsWith('UK') || this.verificationType === 'BIOMETRIC_PLUS_ADDRESS_DOCUMENT');
    const defaultCountry = isUK ? 'GB' : 'NZ';
    const defaultFirst = autoCompletionCountries.find(country => country.code === defaultCountry);
    const others = autoCompletionCountries.filter(country => country.code !== defaultCountry);
    this.autoCompletionCountries = defaultFirst ? [defaultFirst, ...others] : others;
    this.buildForm(defaultCountry);
  }

  buildForm(defaultCountry: string = ''): void {
    this.addressForm = this.fb.group({
      'country' : ['', [Validators.required]],
      'fullAddress' : ['', [Validators.required]],
      'unitNumber' : ['', []],
      'streetNumber' : ['', []],
      'streetName' : ['', []],
      'suburb' : ['', []],
      'city' : ['', []],
      'postcode' : ['', []],
      'otherCountry' : ['', []],
    });

    this.addressForm.get('country')
    .valueChanges
    .pipe(takeUntil(this.ngUnsubscribe))
    .subscribe(data => {
      console.log(`Selected country ${data}`);
      this.selectedCountryCode = data;
      this.addressSuggestions = [];
      this.addressForm.get('fullAddress')!.setValue('');
    });

    this.addressForm.get('fullAddress')
      .valueChanges
      .pipe(takeUntil(this.ngUnsubscribe), debounceTime(300))
      .subscribe(data => this.updateAddressSuggestions(data as string));

    if (defaultCountry) {
      this.addressForm.get('country')!.setValue(defaultCountry);
    }
  }

  public switchToManual(): void {
    this.addressForm.get('otherCountry').setValue(this.selectedCountryCode);
    this.selectedCountryCode = 'OTHER';
    this.addressForm.get('country').setValue('OTHER');
    this.addressSuggestions = [];
  }

  public updateAddressSuggestions(text: string) {
     if (text && text.length > 2) {
      const matchingAddress = this.addressSuggestions.find (address => address.fullAddress === text);
      if (matchingAddress) {
        return;
      }
    this.addressService.addressSearch(this.invitationId, text, this.selectedCountryCode).then(addresses => {
        this.addressSuggestions = addresses;
      }, err => {
        console.log('Load addresses failed: ' + JSON.stringify(err, null, 4));
      });
    }
    this.addressSuggestions = [];
  }

  public async confirm() {
    try {
      if (this.isSaving) {
        return;
      }
      this.isSaving = true;
      this.showProgressSpinner(this.dialog);
      let residentialAddress: Address;
      if (this.selectedCountryCode === 'OTHER') {
        residentialAddress = this.getEnteredAddress();
      } else {
        const addressText = this.addressForm.value.fullAddress;
        const suggestion = this.addressSuggestions.find(suggestion => suggestion.fullAddress === addressText);
        if (suggestion) {
          residentialAddress = await this.addressService.addressLookUp(suggestion, this.selectedCountryCode)
        }
      }
      console.log(`Confirmed ${JSON.stringify(residentialAddress, null, 4)}`);
      const invitationId = this.route.snapshot.paramMap.get('invitationId');
      await this.verificationClientService.saveAddress(invitationId, residentialAddress);
      this.hideProgressSpinner();
      this.isSaving = false;
      if (this.verificationType === 'UK_STANDARD_BIOMETRIC' || this.verificationType === 'BIOMETRIC_PLUS_ADDRESS_DOCUMENT' || this.verificationType === 'AU_SUPPLEMENTED_BIOMETRIC') {
        this.router.navigate(['client/capture/', this.invitationId], { replaceUrl: true, queryParams: { captureUrl: this.captureUrl, verificationType: this.verificationType } });
      } else if (this.selectedCountryCode === 'AU' && this.auPayrollSuperConsent === 'true') {
        this.router.navigate(['client/au-consent/', this.invitationId], { replaceUrl: true, queryParams: { captureUrl: this.captureUrl } });
      } else {
        // Note: this call is async so don't do anything after it without adding an await
        await this.forwardToBiometric(this.document, this.invitationService, this.invitationId, this.captureUrl, this.dialog);
      }
    } catch(err) {
      console.log(`Error ${err.message}`);
      this.hideProgressSpinner();
      this.isSaving = false;
      this.showAlert(this.dialog, 'PROBLEM', `Failed to save address. Press try again.`);
    };
  }

  private getEnteredAddress(): Address {
    const address: Address = {
      unitNumber: this.addressForm.value.unitNumber,
      streetNumber: this.addressForm.value.streetNumber,
      streetName: this.addressForm.value.streetName,
      streetType: '',
      suburb: this.addressForm.value.suburb,
      city: this.addressForm.value.city,
      postcode: this.addressForm.value.postcode,
      country: this.addressForm.value.otherCountry,
    };
    return address;
  }
}
